// =====================================================================
// Assessment Center — Grading domain: shared attempts repository.
//
// The one in-memory store both the instructor and student feature
// modules read/write for exam attempts, answers, essay reviews,
// grades, and attachments. It also holds a read-model "catalog"
// (exams/sections/examQuestions/questions/mc answers/essay details)
// used to grade and to display exam content. See CHANGELOG.md for why
// this consolidation happened.
//
// Every shape here matches src/shared/types/db.ts exactly — no schema
// drift. Swapping this file's internals for real D1-backed Worker
// fetch() calls (and dropping the catalog seed, which would then come
// from the real `questions`/`exams` tables) is the intended path to a
// live backend; every exported function here is already shaped like
// the API call it will become. See DEPLOYMENT.md.
// =====================================================================
import type {
  Exam,
  ExamSection,
  ExamQuestion,
  ExamAttempt,
  ExamAttemptStatus,
  Question,
  MultipleChoiceAnswer,
  EssayQuestion,
  StudentAnswer,
  EssayReview,
  Grade,
  StudentAnswerAttachment,
} from "../types/db";

const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const nowIso = () => new Date().toISOString();

// ---------------------------------------------------------------------
// Catalog (read-model) — seed data for the exams/questions used
// throughout this project's sample data.
// ---------------------------------------------------------------------
const exams: Exam[] = [
  {
    id: "exam_1",
    course_id: "course_1",
    created_by: "teacher_1",
    title: "Air Law — Midterm Assessment",
    description: "Covers ICAO Annexes 1, 2, and 6.",
    exam_type: "mixed",
    status: "published",
    duration_minutes: 45,
    passing_score: 8,
    max_attempts: 2,
    start_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    end_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: nowIso(),
    updated_at: nowIso(),
  },
  {
    id: "exam_2",
    course_id: "course_2",
    created_by: "teacher_1",
    title: "Meteorology — Quick Check",
    description: "Short METAR/TAF and cloud-type knowledge check.",
    exam_type: "multiple_choice",
    status: "published",
    duration_minutes: 15,
    passing_score: 2,
    max_attempts: 1,
    start_at: null,
    end_at: null,
    created_at: nowIso(),
    updated_at: nowIso(),
  },
];

const examSections: ExamSection[] = [
  { id: "sec_1", exam_id: "exam_1", title: "Multiple Choice", instructions: "Choose the single best answer for each question.", section_type: "multiple_choice", order_index: 0, time_limit_minutes: null },
  { id: "sec_2", exam_id: "exam_1", title: "Written Response", instructions: "Answer in full sentences. Cite regulation numbers where relevant.", section_type: "essay", order_index: 1, time_limit_minutes: null },
  { id: "sec_3", exam_id: "exam_2", title: "Multiple Choice", instructions: "Choose the single best answer for each question.", section_type: "multiple_choice", order_index: 0, time_limit_minutes: null },
];

const examQuestions: ExamQuestion[] = [
  { id: "eq_1", exam_section_id: "sec_1", question_id: "q_1", order_index: 0, points_override: null },
  { id: "eq_2", exam_section_id: "sec_1", question_id: "q_2", order_index: 1, points_override: null },
  { id: "eq_3", exam_section_id: "sec_2", question_id: "q_3", order_index: 0, points_override: 15 },
  { id: "eq_4", exam_section_id: "sec_3", question_id: "q_4", order_index: 0, points_override: null },
  { id: "eq_5", exam_section_id: "sec_3", question_id: "q_5", order_index: 1, points_override: null },
];

const questions: Question[] = [
  { id: "q_1", question_bank_id: "bank_1", created_by: "teacher_1", type: "multiple_choice", prompt: "Under ICAO Annex 2, VFR flight is prohibited above which flight level unless authorized?", difficulty: "medium", default_points: 1, media_url: null, status: "published", created_at: nowIso(), updated_at: nowIso() },
  { id: "q_2", question_bank_id: "bank_1", created_by: "teacher_1", type: "multiple_choice", prompt: "Which document must a pilot-in-command carry for an international flight?", difficulty: "easy", default_points: 1, media_url: null, status: "published", created_at: nowIso(), updated_at: nowIso() },
  { id: "q_3", question_bank_id: "bank_1", created_by: "teacher_1", type: "essay", prompt: "Explain the difference between controlled and uncontrolled airspace, and describe how ATC separation minima change between the two.", difficulty: "hard", default_points: 10, media_url: null, status: "published", created_at: nowIso(), updated_at: nowIso() },
  { id: "q_4", question_bank_id: "bank_2", created_by: "teacher_1", type: "multiple_choice", prompt: "In a METAR, what does the group 'BKN018' indicate?", difficulty: "medium", default_points: 1, media_url: null, status: "published", created_at: nowIso(), updated_at: nowIso() },
  { id: "q_5", question_bank_id: "bank_2", created_by: "teacher_1", type: "multiple_choice", prompt: "Which cloud type is most commonly associated with severe turbulence and hail?", difficulty: "medium", default_points: 1, media_url: null, status: "published", created_at: nowIso(), updated_at: nowIso() },
];

const mcAnswers: MultipleChoiceAnswer[] = [
  { id: "a_1", question_id: "q_1", answer_text: "FL 195", is_correct: false, order_index: 0 },
  { id: "a_2", question_id: "q_1", answer_text: "FL 200", is_correct: true, order_index: 1 },
  { id: "a_3", question_id: "q_1", answer_text: "FL 245", is_correct: false, order_index: 2 },
  { id: "a_4", question_id: "q_1", answer_text: "FL 290", is_correct: false, order_index: 3 },
  { id: "a_5", question_id: "q_2", answer_text: "Certificate of Airworthiness only", is_correct: false, order_index: 0 },
  { id: "a_6", question_id: "q_2", answer_text: "Journey Log only", is_correct: false, order_index: 1 },
  { id: "a_7", question_id: "q_2", answer_text: "Certificate of Registration and Airworthiness, and crew licences", is_correct: true, order_index: 2 },
  { id: "a_8", question_id: "q_4", answer_text: "Broken cloud, base 1,800 ft", is_correct: true, order_index: 0 },
  { id: "a_9", question_id: "q_4", answer_text: "Broken cloud, base 18,000 ft", is_correct: false, order_index: 1 },
  { id: "a_10", question_id: "q_4", answer_text: "Blowing snow, visibility 1800 m", is_correct: false, order_index: 2 },
  { id: "a_11", question_id: "q_5", answer_text: "Cirrus", is_correct: false, order_index: 0 },
  { id: "a_12", question_id: "q_5", answer_text: "Cumulonimbus", is_correct: true, order_index: 1 },
  { id: "a_13", question_id: "q_5", answer_text: "Stratus", is_correct: false, order_index: 2 },
  { id: "a_14", question_id: "q_5", answer_text: "Altocumulus", is_correct: false, order_index: 3 },
];

const essayDetails: EssayQuestion[] = [
  {
    question_id: "q_3",
    model_answer: "Controlled airspace requires ATC clearance and separation is actively provided; uncontrolled airspace relies on see-and-avoid...",
    grading_rubric: "Award points for: definition accuracy, separation minima comparison, real-world example.",
    min_words: 120,
    max_words: 400,
  },
];

// ---------------------------------------------------------------------
// Transactional tables (mutable)
// ---------------------------------------------------------------------
let examAttempts: ExamAttempt[] = [];
let studentAnswers: StudentAnswer[] = [];
let essayReviews: EssayReview[] = [];
let grades: Grade[] = [];
let studentAnswerAttachments: StudentAnswerAttachment[] = [];

// ---------------------------------------------------------------------
// Catalog reads
// ---------------------------------------------------------------------
export function getExam(examId: string): Exam | undefined {
  return exams.find((e) => e.id === examId);
}
export function getExamSections(examId: string): ExamSection[] {
  return examSections.filter((s) => s.exam_id === examId).sort((a, b) => a.order_index - b.order_index);
}
export function getExamQuestions(sectionId: string): ExamQuestion[] {
  return examQuestions.filter((eq) => eq.exam_section_id === sectionId).sort((a, b) => a.order_index - b.order_index);
}
export function getExamQuestion(examQuestionId: string): ExamQuestion | undefined {
  return examQuestions.find((eq) => eq.id === examQuestionId);
}
export function getQuestion(questionId: string): Question | undefined {
  return questions.find((q) => q.id === questionId);
}
export function getMcAnswers(questionId: string): MultipleChoiceAnswer[] {
  return mcAnswers.filter((a) => a.question_id === questionId).sort((a, b) => a.order_index - b.order_index);
}
export function getCorrectAnswerId(questionId: string): string | null {
  return mcAnswers.find((a) => a.question_id === questionId && a.is_correct)?.id ?? null;
}
export function getEssayDetail(questionId: string): EssayQuestion | undefined {
  return essayDetails.find((e) => e.question_id === questionId);
}
export function listPublishedExams(): Exam[] {
  return exams.filter((e) => e.status === "published" || e.status === "active");
}
export function pointsForExamQuestion(eq: ExamQuestion): number {
  const q = getQuestion(eq.question_id);
  return eq.points_override ?? q?.default_points ?? 0;
}

// ---------------------------------------------------------------------
// exam_attempts
// ---------------------------------------------------------------------
export function listAttempts(filter: { studentId?: string; examId?: string; status?: ExamAttemptStatus } = {}): ExamAttempt[] {
  return examAttempts
    .filter((a) => !filter.studentId || a.student_id === filter.studentId)
    .filter((a) => !filter.examId || a.exam_id === filter.examId)
    .filter((a) => !filter.status || a.status === filter.status)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
}
export function getAttempt(attemptId: string): ExamAttempt | undefined {
  return examAttempts.find((a) => a.id === attemptId);
}
export function insertAttempt(attempt: ExamAttempt): ExamAttempt {
  examAttempts = [attempt, ...examAttempts];
  return attempt;
}
export function updateAttempt(attemptId: string, patch: Partial<ExamAttempt>): ExamAttempt {
  const idx = examAttempts.findIndex((a) => a.id === attemptId);
  if (idx === -1) throw new Error("Attempt not found.");
  examAttempts[idx] = { ...examAttempts[idx], ...patch };
  return examAttempts[idx];
}

// ---------------------------------------------------------------------
// student_answers
// ---------------------------------------------------------------------
export function listStudentAnswers(attemptId: string): StudentAnswer[] {
  return studentAnswers.filter((sa) => sa.exam_attempt_id === attemptId);
}
export function getStudentAnswer(attemptId: string, examQuestionId: string): StudentAnswer | undefined {
  return studentAnswers.find((sa) => sa.exam_attempt_id === attemptId && sa.exam_question_id === examQuestionId);
}
export function getStudentAnswerById(studentAnswerId: string): StudentAnswer | undefined {
  return studentAnswers.find((sa) => sa.id === studentAnswerId);
}
export function upsertStudentAnswer(input: {
  exam_attempt_id: string;
  exam_question_id: string;
  selected_answer_id?: string | null;
  essay_answer_text?: string | null;
}): StudentAnswer {
  const idx = studentAnswers.findIndex(
    (sa) => sa.exam_attempt_id === input.exam_attempt_id && sa.exam_question_id === input.exam_question_id
  );
  const base: StudentAnswer = {
    id: idx >= 0 ? studentAnswers[idx].id : uuid(),
    exam_attempt_id: input.exam_attempt_id,
    exam_question_id: input.exam_question_id,
    selected_answer_id: input.selected_answer_id ?? (idx >= 0 ? studentAnswers[idx].selected_answer_id : null),
    essay_answer_text: input.essay_answer_text ?? (idx >= 0 ? studentAnswers[idx].essay_answer_text : null),
    is_correct: idx >= 0 ? studentAnswers[idx].is_correct : null,
    points_awarded: idx >= 0 ? studentAnswers[idx].points_awarded : null,
    answered_at: nowIso(),
  };
  if (idx >= 0) studentAnswers[idx] = base;
  else studentAnswers.push(base);
  return base;
}
/** Used only by the grading service to write computed is_correct/points_awarded. */
export function setAnswerGrade(answerId: string, patch: { is_correct: boolean | null; points_awarded: number | null }): void {
  const idx = studentAnswers.findIndex((sa) => sa.id === answerId);
  if (idx === -1) return;
  studentAnswers[idx] = { ...studentAnswers[idx], ...patch };
}

// ---------------------------------------------------------------------
// essay_reviews
// ---------------------------------------------------------------------
export function listEssayReviews(attemptId: string): (EssayReview & { exam_question_id: string })[] {
  const answerIds = new Set(studentAnswers.filter((sa) => sa.exam_attempt_id === attemptId).map((sa) => sa.id));
  return essayReviews
    .filter((r) => answerIds.has(r.student_answer_id))
    .map((r) => {
      const sa = studentAnswers.find((sa) => sa.id === r.student_answer_id)!;
      return { ...r, exam_question_id: sa.exam_question_id };
    });
}
export function getEssayReviewByAnswer(studentAnswerId: string): EssayReview | undefined {
  return essayReviews.find((r) => r.student_answer_id === studentAnswerId);
}
export function createPendingEssayReview(studentAnswerId: string, reviewerId: string): EssayReview {
  const existing = getEssayReviewByAnswer(studentAnswerId);
  if (existing) return existing;
  const review: EssayReview = {
    id: uuid(),
    student_answer_id: studentAnswerId,
    reviewer_id: reviewerId,
    score: null,
    feedback: null,
    status: "pending",
    reviewed_at: null,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  essayReviews.push(review);
  return review;
}
export function completeEssayReview(reviewId: string, input: { score: number; feedback: string }): EssayReview {
  const idx = essayReviews.findIndex((r) => r.id === reviewId);
  if (idx === -1) throw new Error("Review not found.");
  essayReviews[idx] = {
    ...essayReviews[idx],
    score: input.score,
    feedback: input.feedback,
    status: "completed",
    reviewed_at: nowIso(),
    updated_at: nowIso(),
  };
  return essayReviews[idx];
}

// ---------------------------------------------------------------------
// grades
// ---------------------------------------------------------------------
export function getGrade(attemptId: string): Grade | undefined {
  return grades.find((g) => g.exam_attempt_id === attemptId);
}
export function upsertGrade(input: Omit<Grade, "id" | "graded_at"> & { id?: string }): Grade {
  const idx = grades.findIndex((g) => g.exam_attempt_id === input.exam_attempt_id);
  const record: Grade = {
    id: idx >= 0 ? grades[idx].id : uuid(),
    exam_attempt_id: input.exam_attempt_id,
    student_id: input.student_id,
    total_score: input.total_score,
    max_score: input.max_score,
    percentage: input.percentage,
    passed: input.passed,
    graded_at: nowIso(),
  };
  if (idx >= 0) grades[idx] = record;
  else grades.push(record);
  return record;
}

// ---------------------------------------------------------------------
// student_answer_attachments (migration 0002)
// ---------------------------------------------------------------------
export function listAttachments(studentAnswerId: string): StudentAnswerAttachment[] {
  return studentAnswerAttachments
    .filter((a) => a.student_answer_id === studentAnswerId)
    .sort((a, b) => a.uploaded_at.localeCompare(b.uploaded_at));
}
export function addAttachment(input: {
  student_answer_id: string;
  file_type: StudentAnswerAttachment["file_type"];
  file_name: string;
  file_url: string;
}): StudentAnswerAttachment {
  const record: StudentAnswerAttachment = { id: uuid(), uploaded_at: nowIso(), ...input };
  studentAnswerAttachments.push(record);
  return record;
}
